import { Link } from "react-router-dom";
import { useState } from "react";
import {
    Mail,
    Phone,
    MapPin,
    ArrowRight,
    Facebook,
    Instagram,
    Linkedin,
    Twitter,
    MessageCircle,
    Code,
    Shield,
    Zap,
    Award,
    ExternalLink,
    Loader2
} from "lucide-react";
import { newsletterAPI } from "../services/newsletterAPI";
import { toast } from "sonner";

const Footer = () => {
    const currentYear = new Date().getFullYear();
    const [email, setEmail] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const handleNewsletterSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!email || !email.trim()) {
            toast.error('Por favor, insira um email válido');
            return;
        }

        setIsLoading(true);

        try {
            const response = await newsletterAPI.subscribe(email);

            if (response.success) {
                toast.success(response.message || 'Inscrição realizada com sucesso!');
                setEmail('');
            } else {
                toast.error(response.message || 'Erro ao se inscrever na newsletter');
            }
        } catch (error: unknown) {
            const errorMessage = (error as any).response?.data?.message || 'Erro ao se inscrever. Tente novamente mais tarde.';
            toast.error(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    const footerSections = [
        {
            title: "Serviços",
            items: [
                { label: "Desenvolvimento Web", href: "/servicos", icon: Code },
                { label: "Apps Mobile", href: "/servicos", icon: Zap },
                { label: "Sistemas ERP/CRM", href: "/servicos", icon: Shield },
                { label: "Cibersegurança", href: "/servicos", icon: Shield },
                { label: "Automação", href: "/servicos", icon: Award }
            ]
        },
        {
            title: "Empresa",
            items: [
                { label: "Sobre Nós", href: "/sobre", icon: ArrowRight },
                { label: "Portfólio", href: "/portfolio", icon: ArrowRight },
                { label: "FAQ", href: "/FAQ", icon: ArrowRight },
                { label: "Contato", href: "/contato", icon: ArrowRight }
            ]
        },
        {
            title: "Plataforma",
            items: [
                { label: "Como Orçamos", href: "/como-orcamos", icon: ArrowRight },
                { label: "Dashboard Cliente", href: "/client", icon: ArrowRight },
            ]
        }
    ];

    const socialLinks = [
        {
            name: "Facebook",
            href: "https://facebook.com/alphalabs",
            icon: Facebook,
            color: "hover:text-blue-500"
        },
        {
            name: "Instagram",
            href: "https://instagram.com/alphalabs",
            icon: Instagram,
            color: "hover:text-pink-500"
        },
        {
            name: "LinkedIn",
            href: "https://linkedin.com/company/alphalabs",
            icon: Linkedin,
            color: "hover:text-blue-600"
        },
        {
            name: "Twitter",
            href: "https://twitter.com/alphalabs",
            icon: Twitter,
            color: "hover:text-blue-400"
        },
        {
            name: "WhatsApp",
            href: "https://wa.me/5573988188407",
            icon: MessageCircle,
            color: "hover:text-green-500"
        }
    ];

    const contactInfo = [
        {
            icon: Mail,
            label: "Email",
            value: "alphalabsia@gmail.com",
            href: "mailto:alphalabsia@gmail.com"
        },
        {
            icon: Phone,
            label: "Telefone",
            value: "(73) 98818-8407",
            href: "tel:+5573988188407"
        },
        {
            icon: MapPin,
            label: "Endereço",
            value: " BA - Brasil",
            href: "https://www.google.com/maps/place/Av.+Sete+de+Setembro,+4152-4300+-+Barra,+Salvador+-+BA/@-13.0073997,-38.5430823,15z/data=!3m1!4b1!4m6!3m5!1s0x7160384ab7f087d:0xa8130be88310b77!8m2!3d-13.0074208!4d-38.5327826!16s%2Fg%2F11g610_6nx?entry=ttu&g_ep=EgoyMDI1MDkwMy4wIKXMDSoASAFQAw%3D%3D"
        }
    ];

    return (
        <footer className="relative bg-gradient-to-br from-background via-muted/20 to-background border-t border-border/50">
            {/* Background Effects */}
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute top-0 left-1/4 w-64 h-64 bg-primary/5 rounded-full filter blur-3xl opacity-60"></div>
                <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-accent/5 rounded-full filter blur-3xl opacity-60"></div>
            </div>

            <div className="container-page relative z-10">
                {/* Main Footer Content */}
                <div className="py-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-6 lg:gap-6">

                    {/* Company Info - 4 columns */}
                    <div className="lg:col-span-4 space-y-6 text-center lg:text-left">
                        <div className="space-y-4">
                            <div className="flex items-center justify-center lg:justify-start">
                                <div className="w-10 h-10 rounded-xl overflow-hidden flex-shrink-0">
                                    <img
                                        src="https://ik.imagekit.io/AlphaLabs/logo.webp?tr=w-180,q-90,f-webp"
                                        alt="AlphaLabs Logo"
                                        className="w-full h-full object-contain"
                                    />
                                </div>
                                <h3 className="text-xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                                    AlphaLabs
                                </h3>
                            </div>

                            <p className="text-muted-foreground leading-relaxed max-w-sm">
                                Somos uma empresa de tecnologia que constrói produtos digitais e plataformas sob medida com foco em
                                <strong className="text-primary"> performance</strong>, <strong className="text-primary">segurança</strong> e
                                <strong className="text-primary"> escalabilidade</strong>.
                            </p>
                        </div>

                        {/* Contact Info */}
                        <div className="space-y-3 flex flex-col items-center lg:items-start">
                            {contactInfo.map((contact, index) => (
                                <a
                                    key={index}
                                    href={contact.href}
                                    className="flex items-center gap-3 text-sm text-muted-foreground hover:text-primary transition-colors duration-300 group"
                                    target={contact.href.startsWith('http') ? '_blank' : undefined}
                                    rel={contact.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                                >
                                    <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                                        <contact.icon className="w-4 h-4 text-primary" />
                                    </div>
                                    <div>
                                        <div className="font-medium text-foreground">{contact.value}</div>
                                    </div>
                                </a>
                            ))}
                        </div>
                    </div>

                    {/* Footer Sections - 8 columns total */}
                    {footerSections.map((section, index) => (
                        <div key={index} className="lg:col-span-2 lg:col-start-auto space-y-4">
                            <h4 className="font-semibold text-lg text-foreground text-center lg:text-left">{section.title}</h4>
                            <ul className="space-y-3 text-center lg:text-left">
                                {section.items.map((item, itemIndex) => (
                                    <li key={itemIndex}>
                                        <Link
                                            to={item.href}
                                            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors duration-300 group justify-center lg:justify-start"
                                        >
                                            <item.icon className="w-3 h-3 opacity-60 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
                                            <span className="group-hover:translate-x-1 transition-transform">{item.label}</span>
                                        </Link>
                                    </li>
                                ))}<div className="flex items-center gap-3"></div>
                            </ul>
                        </div>
                    ))}

{/* Newsletter - 2 columns */}
<div className="md:col-span-2 lg:col-span-2 space-y-4 text-center lg:text-left">
    <div className="md:max-w-sm md:mx-auto lg:max-w-none lg:mx-0">
        <h4 className="font-semibold text-lg text-foreground">Newsletter</h4>
        <p className="text-sm text-muted-foreground">
            Receba as últimas novidades e dicas sobre tecnologia.
        </p>
        <form onSubmit={handleNewsletterSubmit} className="space-y-3">
            <div className="flex flex-col gap-2">
                <input
                    type="email"
                    placeholder="Seu melhor e-mail"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={isLoading}
                    className="px-4 py-2 bg-background border border-border rounded-lg focus:border-primary focus:outline-none transition-colors text-sm w-full disabled:opacity-50 disabled:cursor-not-allowed"
                />
                <button
                    type="submit"
                    disabled={isLoading}
                    className="px-4 py-2 bg-gradient-to-r from-primary to-accent text-white rounded-lg font-medium hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl text-sm w-full disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center justify-center gap-2"
                >
                    {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                    {isLoading ? 'Inscrevendo...' : 'Inscrever-se'}
                </button>
            </div>
            <p className="text-xs text-muted-foreground">
                Não enviamos spam. Cancele quando quiser.
            </p>
        </form>
    </div>
</div>
                </div>

                {/* Social Links & Legal */}
                <div className="text-center space-y-4 py-4">
                    <h4 className="font-semibold text-foreground">Siga-nos</h4>
                    <div className="flex gap-3 justify-center">
                        {socialLinks.map((social, index) => (
                            <a
                                key={index}
                                href={social.href}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`w-10 h-10 rounded-lg bg-muted/50 hover:bg-primary/10 flex items-center justify-center transition-all duration-300 hover:scale-110 hover:shadow-lg text-muted-foreground ${social.color} group`}
                                aria-label={social.name}
                            >
                                <social.icon className="w-5 h-5 group-hover:scale-110 transition-transform" />
                            </a>
                        ))}
                    </div>

                    {/* Legal Links */}
                    <div className="flex justify-center gap-6 text-sm text-muted-foreground pt-2">
                        <Link to="https://drive.google.com/file/d/1cNQHFEjoE9cSHoePseuca0ralOGovBFA/view?usp=drive_link" className="hover:text-primary transition-colors">
                            Termos de Uso
                        </Link>
                        <Link to="/privacy-policy" className="hover:text-primary transition-colors font-medium">
                            Política de Privacidade
                        </Link>
                    </div>
                </div>

                {/* Bottom Bar */}
                <div className="border-t border-border/50 bg-gradient-to-r from-primary/5 via-transparent to-accent/5 py-4">
                    <div className="text-center space-y-2">
                        <div className="text-sm text-muted-foreground">
                            © 2025{currentYear > 2025 ? ` - ${currentYear}` : ''} AlphaLabs. Todos os direitos reservados.
                        </div>

                        <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                            <span>Desenvolvido por:</span>
                            <a
                                href="https://www.linkedin.com/in/wallax-figueiredo-41116b285/"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-1 text-primary hover:text-primary/80 font-semibold transition-colors group"
                            >
                                Wallcod
                                <ExternalLink className="w-3 h-3 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                            </a>
                            <span>🦁</span>
                        </div>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;